import { useState } from 'react'
import { Typography, Breadcrumb, Table, Button, Modal, Select, Input, Space, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import {
  useV20TfomsStyle,
  useCreateV20TfomsRecord,
  useUpdateV20TfomsRecord,
  useDeleteV20TfomsRecord,
} from '@/hooks/useNsiTfoms'
import { useAuthStore } from '@/store/auth'
import type { V20TfomsStyleItem, V020Suggestion } from '@/api/nsiTfoms'

const { Title } = Typography

export default function NsiTfomsProfilesPage() {
  const user = useAuthStore((s) => s.user)
  const isSuperadmin = user?.role === 'superadmin'

  const { data, isLoading, refetch } = useV20TfomsStyle()
  const createMutation = useCreateV20TfomsRecord()
  const updateMutation = useUpdateV20TfomsRecord()
  const deleteMutation = useDeleteV20TfomsRecord()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState<number | null>(null)
  const [customName, setCustomName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingGroupCode, setEditingGroupCode] = useState<number | null>(null)
  const [customGroupCode, setCustomGroupCode] = useState<number | undefined>(undefined)

  const items = data?.items || []
  const suggestions = data?.suggestions || []

  const handleAdd = () => {
    setIsModalVisible(true)
    setSelectedSuggestion(null)
    setCustomName('')
  }

  const handleSuggestionSelect = (value: number) => {
    setSelectedSuggestion(value)
    const suggestion = suggestions.find((s) => s.idk_pr === value)
    if (suggestion) {
      setCustomName(suggestion.suggested_tfoms_name)
    }
  }

  const handleCreate = async () => {
    if (!selectedSuggestion || !customName) {
      return
    }

    await createMutation.mutateAsync({
      idpk_tfoms: selectedSuggestion,
      profil_k_tfoms_name: customName,
      tfoms_group_code: customGroupCode ?? selectedSuggestion,
    })

    setIsModalVisible(false)
    setCustomGroupCode(undefined)
    refetch()
  }

  const handleEdit = (record: V20TfomsStyleItem) => {
    setEditingId(record.idpk_tfoms)
    setEditingName(record.profil_k_tfoms_name || '')
    setEditingGroupCode(record.tfoms_group_code)
  }

  const handleSaveEdit = async (idpk_tfoms: number) => {
    await updateMutation.mutateAsync({
      idpk_tfoms,
      data: {
        profil_k_tfoms_name: editingName,
        tfoms_group_code: editingGroupCode ?? undefined,
      },
    })

    setEditingId(null)
    setEditingName('')
    setEditingGroupCode(null)
    refetch()
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingName('')
    setEditingGroupCode(null)
  }

  const handleDelete = async (idpk_tfoms: number) => {
    await deleteMutation.mutateAsync(idpk_tfoms)
    refetch()
  }

  const columns: ColumnsType<V20TfomsStyleItem> = [
    {
      title: 'Код ТФОМС',
      dataIndex: 'idpk_tfoms',
      key: 'idpk_tfoms',
      width: 120,
      sorter: (a, b) => a.idpk_tfoms - b.idpk_tfoms,
    },
    {
      title: 'Название ТФОМС',
      dataIndex: 'profil_k_tfoms_name',
      key: 'profil_k_tfoms_name',
      render: (text: string | null, record: V20TfomsStyleItem) => {
        if (editingId === record.idpk_tfoms) {
          return (
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onPressEnter={() => handleSaveEdit(record.idpk_tfoms)}
              onBlur={handleCancelEdit}
              autoFocus
            />
          )
        }
        return text || '-'
      },
    },
    {
      title: 'Группа ТФОМС',
      dataIndex: 'tfoms_group_code',
      key: 'tfoms_group_code',
      width: 130,
      render: (code: number, record: V20TfomsStyleItem) => {
        if (editingId === record.idpk_tfoms) {
          return (
            <Input
              type="number"
              value={editingGroupCode ?? ''}
              onChange={(e) => setEditingGroupCode(Number(e.target.value) || null)}
              style={{ width: 80 }}
            />
          )
        }
        return code !== record.idpk_tfoms ? (
          <span style={{ color: '#1677ff', fontWeight: 500 }}>{code}</span>
        ) : code
      },
      sorter: (a, b) => a.tfoms_group_code - b.tfoms_group_code,
    },
    {
      title: 'Код V020',
      dataIndex: 'v020_code',
      key: 'v020_code',
      width: 120,
      render: (code: number | null) => code || '-',
    },
    {
      title: 'Название V020',
      dataIndex: 'v020_name',
      key: 'v020_name',
      render: (name: string | null) => name || '-',
    },
    ...(isSuperadmin ? [{
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, record: V20TfomsStyleItem) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Удалить запись?"
            description="Это действие нельзя отменить"
            onConfirm={() => handleDelete(record.idpk_tfoms)}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ]

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Администрирование</Breadcrumb.Item>
        <Breadcrumb.Item>Справочники НСИ</Breadcrumb.Item>
        <Breadcrumb.Item>Профили ТФОМС</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Профили ТФОМС (v20_tfoms_style)</Title>
        {isSuperadmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Добавить маппинг
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="idpk_tfoms"
        loading={isLoading}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => `Всего записей: ${total}`,
        }}
      />

      <Modal
        title="Добавить маппинг профиля"
        open={isModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !selectedSuggestion || !customName }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <label>Код V020 (недостающие в ТФОМС):</label>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Выберите код V020"
              value={selectedSuggestion}
              onChange={handleSuggestionSelect}
              showSearch
              optionFilterProp="children"
            >
              {suggestions.map((s: V020Suggestion) => (
                <Select.Option key={s.idk_pr} value={s.idk_pr}>
                  {s.idk_pr} - {s.k_prname}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label>Название для ТФОМС:</label>
            <Input
              style={{ marginTop: 8 }}
              placeholder="Введите название"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
          </div>

          <div>
            <label>Код группы ТФОМС (для агрегации):</label>
            <Input
              type="number"
              style={{ marginTop: 8 }}
              placeholder={selectedSuggestion ? String(selectedSuggestion) : 'Код группы'}
              value={customGroupCode}
              onChange={(e) => setCustomGroupCode(e.target.value ? Number(e.target.value) : undefined)}
            />
            <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
              По умолчанию = код V020. Измените для агрегации (напр. онко-хирургия 44-48 → 43)
            </div>
          </div>

          {selectedSuggestion && (
            <div style={{ marginTop: 8, padding: 8, background: '#f0f0f0', borderRadius: 4 }}>
              <small>Предложенное название: {customName}</small>
            </div>
          )}
        </Space>
      </Modal>
    </div>
  )
}
